<template>
  <component
    v-if="componentName"
    :is="componentName"
    :payload="section.payload"
    :fields="section.fields"
  ></component>
  <field-catalog-section
    v-else-if="section.fields && section.fields.length > 0"
    :section-key="section.sectionKey"
    :fields="section.fields"
  ></field-catalog-section>
</template>

<script>
// The one dispatch point per {SectionKey, Payload|Fields} (DETAIL_PAGE_DESIGN_FE.md),
// mirroring components/Search/FilterField.vue's "type decides which component" pattern
// applied to SectionKey instead of filterType. Adding a new typed
// section only ever means one new entry here + one new component under ./typedSections — never
// touching ProductDetails.vue. Unknown/absent SectionKeys (a catalog row this page hasn't built a
// component for yet) simply don't render — no per-asset-class v-if anywhere in this file or its
// children.
//
// Both `payload` and `fields` are always passed to the dynamic component — most typed sections are
// backend-Payload-shaped and only declare a `payload` prop (the other stays undefined, harmless), but
// a typed section can also exist purely for a *presentation* reason on a Fields-shaped section the
// backend itself treats as plain field-catalog (e.g. Liquidity → LiquiditySection.vue, which renders
// a slider instead of FieldCatalogSection's label/value rows) — that one declares a `fields` prop
// instead.
import FieldCatalogSection from './FieldCatalogSection'
import LiquiditySection from './typedSections/LiquiditySection'
import RiskProfileSection from './typedSections/RiskProfileSection'
import CostsProfileSection from './typedSections/CostsProfileSection'
import PerformanceScenariosSection from './typedSections/PerformanceScenariosSection'
import PerformanceIndicatorsSection from './typedSections/PerformanceIndicatorsSection'
import UnderlyingsSection from './typedSections/UnderlyingsSection'
import CostsAtExpirationSection from './typedSections/CostsAtExpirationSection'
import TargetMarketSection from './typedSections/TargetMarketSection'
import ServicesSection from './typedSections/ServicesSection'
import AdditionalInfoSection from './typedSections/AdditionalInfoSection'
import SustainabilitySection from './typedSections/SustainabilitySection'
import InvestmentOptionsProfileSection from './typedSections/InvestmentOptionsProfileSection'
import InvestmentOptionsListSection from './typedSections/InvestmentOptionsListSection'

const SECTION_COMPONENT_BY_KEY = {
  Liquidity: 'liquiditySection',
  RiskProfile: 'riskProfileSection',
  CostsProfile: 'costsProfileSection',
  PerformanceScenarios: 'performanceScenariosSection',
  PerformanceIndicators: 'performanceIndicatorsSection',
  Underlyings: 'underlyingsSection',
  CostsAtExpiration: 'costsAtExpirationSection',
  TargetMarket: 'targetMarketSection',
  Services: 'servicesSection',
  AdditionalInfo: 'additionalInfoSection',
  Sustainability: 'sustainabilitySection',
  InvestmentOptionsProfile: 'investmentOptionsProfileSection',
  InvestmentOptionsList: 'investmentOptionsListSection',
}

export default {
  name: 'detail-section',
  components: {
    FieldCatalogSection,
    LiquiditySection,
    RiskProfileSection,
    CostsProfileSection,
    PerformanceScenariosSection,
    PerformanceIndicatorsSection,
    UnderlyingsSection,
    CostsAtExpirationSection,
    TargetMarketSection,
    ServicesSection,
    AdditionalInfoSection,
    SustainabilitySection,
    InvestmentOptionsProfileSection,
    InvestmentOptionsListSection,
  },
  props: {
    section: { type: Object, required: true },
  },
  computed: {
    componentName() {
      return SECTION_COMPONENT_BY_KEY[this.section.sectionKey] || null
    },
  },
}
</script>
